import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import TeamsLogo from '/images/teams.svg';
import TagsField from '../../../core/components/Fields/TagsField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Switch from '../../../core/components/Switch.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { invalidRecipients, parseRecipientList } from '../utils/recipients.ts';
import TeamsChannelPicker from './TeamsChannelPicker.tsx';

const EnvelopeIcon = withIcon(faEnvelope);

const SummaryChannelCard = ({
    form,
    icon,
    title,
    description,
    enabledName,
    children,
}: {
    form: any;
    icon: React.ReactNode;
    title: string;
    description: string;
    enabledName: string;
    children: React.ReactNode;
}) => (
    <div className="rounded-xl flex flex-col gap-3 p-4 border border-line-weak border-solid bg-surface-weak shadow shadow-black/5">
        <Controller
            name={enabledName}
            control={form.control}
            render={({ field }) => (
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                        <span className="w-9 h-9 rounded-md flex items-center justify-center shrink-0 border border-solid border-line-weak bg-surface-weak text-content-medium">
                            {icon}
                        </span>
                        <div className="flex flex-col min-w-0">
                            <h3 className="font-semibold text-content-strong">{title}</h3>
                            <span className="text-sm text-content-medium">{description}</span>
                        </div>
                    </div>
                    <Switch value={field.value} onValueChange={field.onChange} />
                </div>
            )}
        />
        {children}
    </div>
);

const SummaryEmailRecipients = ({ form }) => {
    const { t } = useTranslation('voice_agents');
    const isEnabled: boolean = Boolean(form.watch('summaryEmailEnabled'));

    return (
        <Controller
            name="summaryEmailRecipients"
            control={form.control}
            rules={{
                validate: (value: string[]) => {
                    if (!form.watch('summaryEmailEnabled')) return true;
                    if (parseRecipientList(value).length === 0) {
                        return t('form.summaryRecipientsRequiredError') as string;
                    }
                    const invalid = invalidRecipients(value);
                    return (
                        invalid.length === 0 ||
                        (t('form.summaryRecipientsInvalidError', { emails: invalid.join(', ') }) as string)
                    );
                },
            }}
            render={({ field }) => (
                <FormField
                    label={t('form.summaryRecipients')}
                    required={isEnabled}
                    error={form.formState.errors.summaryEmailRecipients}
                    helpText={t('form.summaryEmailRecipientsHelpText')}
                    className={classnames(!isEnabled && 'opacity-50')}
                >
                    <TagsField
                        {...field}
                        onValueChange={field.onChange}
                        placeholder="sales@company.com"
                        disabled={!isEnabled}
                    />
                </FormField>
            )}
        />
    );
};

const VoiceAgentNotificationsFormFields = ({ form }) => {
    const { t } = useTranslation('voice_agents');

    return (
        <div className="flex flex-col gap-4">
            <p className="text-sm text-content-medium">{t('form.summaryIntro')}</p>
            <SummaryChannelCard
                form={form}
                icon={<EnvelopeIcon />}
                title={t('form.summaryEmailTitle')}
                description={t('form.summaryEmailDescription')}
                enabledName="summaryEmailEnabled"
            >
                <SummaryEmailRecipients form={form} />
            </SummaryChannelCard>
            <SummaryChannelCard
                form={form}
                icon={<img src={TeamsLogo} alt="" className="w-5 h-5" />}
                title={t('form.summaryTeamsTitle')}
                description={t('form.summaryTeamsDescription')}
                enabledName="summaryTeamsEnabled"
            >
                <TeamsChannelPicker form={form} disabled={!form.watch('summaryTeamsEnabled')} />
            </SummaryChannelCard>
        </div>
    );
};

export default VoiceAgentNotificationsFormFields;
